import React, { useState } from 'react';
import { ChevronDownIcon, UserIcon, ShieldCheckIcon, UsersIcon, PencilIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const RoleSwitcher = () => {
  const { user, activeRole, switchRole } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  // Only show role switcher for Super Admins
  if (!user?.isSuperAdmin) {
    return null;
  }

  const availableRoles = [
    {
      key: 'SUPER_ADMIN',
      name: 'Super Admin',
      description: 'Full system access',
      icon: ShieldCheckIcon,
      color: 'text-purple-600'
    },
    {
      key: 'CONTENT_SUPERVISOR',
      name: 'Content Supervisor',
      description: 'Manage content workflow',
      icon: UsersIcon,
      color: 'text-blue-600'
    },
    {
      key: 'CONTENT_GENERATOR',
      name: 'Content Generator',
      description: 'Create questions',
      icon: PencilIcon,
      color: 'text-green-600'
    },
    {
      key: 'CONTENT_REVIEWER',
      name: 'Content Reviewer',
      description: 'Review and approve',
      icon: CheckIcon,
      color: 'text-orange-600'
    }
  ];

  const currentRole = availableRoles.find(role => role.key === activeRole);

  const handleRoleSwitch = (roleKey) => {
    switchRole(roleKey);
    setIsOpen(false);
    // Navigate to dashboard after role switch
    navigate('/dashboard');
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {currentRole && (
          <>
            <currentRole.icon className={`h-4 w-4 ${currentRole.color}`} />
            <span>{currentRole.name}</span>
          </>
        )}
        <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-100 mb-2">
                Switch Role
              </div>

              {availableRoles.map((role) => (
                <button
                  key={role.key}
                  onClick={() => handleRoleSwitch(role.key)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 text-sm rounded-md transition-colors ${
                    activeRole === role.key
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <role.icon className={`h-4 w-4 ${role.color}`} />
                  <div className="flex-1 text-left">
                    <div className="font-medium">{role.name}</div>
                    <div className="text-xs text-slate-500">{role.description}</div>
                  </div>
                  {activeRole === role.key && (
                    <div className="h-2 w-2 bg-blue-600 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RoleSwitcher;
